import { useState, useEffect } from 'react';
import { WifiOff, X } from 'lucide-react';

export default function OfflineAlert() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setDismissed(false); // Reset dismissed state when back online
    };
    const handleOffline = () => {
      setIsOffline(true);
      setDismissed(false); // Show alert again when going offline
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Also check connectivity by pinging a reliable endpoint
    const checkConnection = async () => {
      try {
        await fetch('https://www.google.com/generate_204', {
          method: 'HEAD',
          mode: 'no-cors',
          cache: 'no-store'
        });
        setIsOffline(false);
      } catch {
        setIsOffline(true);
      }
    };

    // Check connection on mount
    checkConnection();

    // Periodically check connection every 30 seconds when offline
    const intervalId = setInterval(() => {
      if (isOffline) {
        checkConnection();
      }
    }, 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, [isOffline]);

  if (!isOffline || dismissed) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-danger text-white px-4 py-3 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <WifiOff className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">No Internet Connection</p>
            <p className="text-sm opacity-90">
              Please check your connection. Some features may not work properly.
            </p>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
